import React from 'react';
import { FiStar, FiArrowRight } from 'react-icons/fi';
import { t } from 'i18next';
import { isPlayfulTheme } from '@/lib/layout-version';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { DashboardDisclaimer } from './dashboard-disclaimer';
import DashboardStudyGuideCard from './dashboard-study-guide-card';
import DashboardShortcutCard from './dashboard-shortcut-card';
import roleta from '@/assets/images/Roleta.png';
import monitor from '@/assets/images/Monitor.png';
import explorarCursos from '@/assets/images/infantil/explorar-cursos.png';
import DashboardCourseCard from './dashboard-course-card';
import { HomeNavCard } from './home/home-nav-card';
import { CardProgressWrapper } from '@/components/ui/card';
import courseDefaultImg from '@/assets/images/course-placeholder.jpg';
import { DashboardShortcutCardWrapper } from '@/components/custom/dashboard';
import { BannerInfo, BannerInfoContent, BannerInfoIcon, BannerInfoTitle, BannerInfoDescription } from '@/components/custom/banner';
import { Button } from '@/components/ui/button';
import { MyProgress, MyProgressHeader, MyProgressTitle } from '@/components/custom/my-progress';
import { DashboardEmptyState, hasDashboardContent } from './dashboard-empty-state';

export function DashboardStudentFeed({ loading, alerts, lastStudyPlan, lastChallenge, liveClassroom, lastCourseWatched, dataCourse, currentStudentSurvey, onOpenSurvey, hideEmptyState }) {
  return (
    <>
      {/* No tema playful esses alertas (desafio/prova prestes a expirar) passaram
          a aparecer no calendário — ver PlayfulCalendarEventCard. */}
      {!isPlayfulTheme() && <DashboardDisclaimer alerts={alerts} />}

      <DashboardStudyGuideCard
        studyPlan={lastStudyPlan?.video}
        id={lastStudyPlan?.id}
      />

      {(lastChallenge || liveClassroom) && (
        <DashboardShortcutCardWrapper>
          {lastChallenge && (
            <DashboardShortcutCard
              source={roleta}
              type="Desafio da semana"
              title={lastChallenge?.title}
              link="/desafio-da-semana/desafios"
              text={lastChallenge?.taxonomy?.name}
            />
          )}

          {liveClassroom && (
            <DashboardShortcutCard
              source={monitor}
              type="Aula ao vivo"
              title={liveClassroom?.title}
              link={`/aulas-ao-vivo/${liveClassroom?.id}`}
              text={`${moment(liveClassroom?.['initial-date']).format('DD/MM/YYYY')} às ${moment(liveClassroom?.['initial-date']).format('HH:mm')}`}
            />
          )}
        </DashboardShortcutCardWrapper>
      )}

      {currentStudentSurvey && (
        <BannerInfo className="mb-6">
          <BannerInfoContent>
            <BannerInfoIcon>
              <FiStar />
            </BannerInfoIcon>
            <BannerInfoTitle>{t('nps.newQuiz')}</BannerInfoTitle>
            <BannerInfoDescription>Queremos saber a sua opinião</BannerInfoDescription>
          </BannerInfoContent>

          <Button
            variant="outline"
            onClick={() => onOpenSurvey()}
          >
            Responder
          </Button>
        </BannerInfo>
      )}

      {lastCourseWatched && Object.keys(lastCourseWatched).length > 0 && (
        <MyProgress>
          <MyProgressHeader>
            <MyProgressTitle>Último curso acessado</MyProgressTitle>

            <Button
              variant="link"
              asChild
            >
              <Link to="/curso">Acesse cursos</Link>
            </Button>
          </MyProgressHeader>

          {isPlayfulTheme() ? (
            // Meia largura: card do último curso + card CTA no MESMO estilo dos
            // cards de atalho da home (HomeNavCard, ex.: Conquistas), pra o card
            // de curso não ocupar a largura toda.
            <div className="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
              {/* Card do último curso em versão minimalista, no mesmo shell
                  compacto do HomeNavCard: thumbnail + título + progresso + seta. */}
              <Link
                to={`/curso/acompanhamento/${dataCourse?.id}`}
                className="group relative flex min-h-[140px] flex-col rounded-[20px] p-5 shadow-card transition-[transform,box-shadow] duration-150 ease-out-strong hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.99]"
                style={{ background: 'var(--color-bg-teal-soft)' }}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={dataCourse?.image?.url || courseDefaultImg}
                    alt=""
                    className="h-12 w-12 shrink-0 rounded-xl object-cover shadow-card"
                  />
                  <div className="min-w-0 flex-1">
                    <span
                      className="block truncate text-base font-extrabold"
                      style={{ color: '#222731' }}
                    >
                      {dataCourse?.title}
                    </span>
                    <span className="mt-0.5 block text-xs font-medium text-neutral-500">
                      {dataCourse?.['finished-learning-system-items']}/{dataCourse?.['learning-system-item-count']} {t('dashboard.completedLessons')}
                    </span>
                  </div>
                </div>
                <div className="mt-auto flex items-center gap-3 pt-4">
                  <div className="min-w-0 flex-1">
                    <CardProgressWrapper
                      percentage={dataCourse?.progress}
                      toFixed={0}
                    />
                  </div>
                  <span
                    aria-hidden="true"
                    className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-white transition-transform duration-100 group-hover:translate-x-0.5"
                    style={{
                      background: 'var(--color-secondary)',
                      boxShadow: '0 3px 0 color-mix(in srgb, var(--color-secondary) 72%, #000), 0 4px 8px rgba(0,0,0,0.18)'
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-x-0 top-0 h-1/2"
                      style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.28) 0%, transparent 100%)' }}
                    />
                    <FiArrowRight className="relative h-4 w-4" />
                  </span>
                </div>
              </Link>
              <HomeNavCard
                to="/curso"
                illustration={explorarCursos}
                title="Explorar mais cursos"
                subtitle="Descubra novos cursos e continue evoluindo no seu ritmo."
                accent={{ bg: 'var(--color-bg-lilac-soft)', arrow: 'var(--color-secondary)' }}
              />
            </div>
          ) : (
            <DashboardCourseCard course={dataCourse} />
          )}
        </MyProgress>
      )}

      {/* Aluno sem nenhuma seção de conteúdo (novo na plataforma ou nada
          liberado ainda): sem isto, o miolo da página fica completamente
          em branco abaixo do banner. */}
      {!loading && !hideEmptyState && !hasDashboardContent({ lastStudyPlan, lastChallenge, liveClassroom, lastCourseWatched }) && <DashboardEmptyState />}
    </>
  );
}
